import { zodResolver } from "@hookform/resolvers/zod";
import type { FlangeClampDefectDetailsData } from "@stryker/core/types/sections";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { createEditJobOptionsQuery } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const cardName = "sealingDetails";

// Flange clamp type groups
const VOID_ONLY_CLAMP_TYPES = ["center blade", "center blade w bars", "one sided", "one sided w bars", "od id w bars"];
const PERIMETER_AND_VOID_CLAMP_TYPES = ["triple seal w tongue", "triple seal"];
const TRIPLE_SEAL_ALLOWED_SEAL_TYPES = ["Packing no groove", "Tubing"];

const PERIMETER_ALLOWED_SEAL_TYPES = [
  "Tapered Crunch Grooves w/ sealant",
  "Sealant",
  "Packing no groove",
  "Tubing",
  "Crunch Grooves w/ sealant",
];

const VOID_ONLY_ALLOWED_SEAL_TYPES = [
  "Crunch Grooves",
  "Tapered Crunch",
  "Crunch Grooves w/ sealant",
  "Tapered Crunch Grooves w/ sealant",
];

const sealingDetailsSchema = z
  .object({
    seal_preference: z.string().min(1, { message: "Seal preference is required" }),
    seal_type: z.string().optional(),
    seal_size: z.string().optional(),
    seal_material: z.string().optional(),
    sealant: z.string().optional(),
    sealant_2: z.string().optional(),
  })
  .refine(
    (data) => {
      const allowedSealTypesForSize = [
        "Packing no groove",
        "Tubing",
        "Sealant",
        "Tapered Crunch Grooves w/ sealant",
        "Crunch Grooves w/ sealant",
      ];
      if (
        data.seal_type &&
        allowedSealTypesForSize.includes(data.seal_type) &&
        (!data.seal_size || data.seal_size === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Seal size is required for this seal type",
      path: ["seal_size"],
    }
  )
  .refine(
    (data) => {
      const allowedSealTypesForMaterial = ["Packing no groove", "Tubing"];
      if (
        data.seal_type &&
        allowedSealTypesForMaterial.includes(data.seal_type) &&
        (!data.seal_material || data.seal_material === "")
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Seal material is required for this seal type",
      path: ["seal_material"],
    }
  );

type SealingDetailsSchema = z.infer<typeof sealingDetailsSchema>;

export function SealDetails() {
  const { sections, toggleExpandableSection, setSealingDetails } = useCUMechanicalJobStore();
  const savedSealingDetails = sections.sealingDetails;
  const repairType = sections.repairType.data.repairType;

  // Only show this component for certain repair types
  const isApplicableRepairType = ["tee", "straight_line", "long_radius", "flange_clamp"].includes(repairType ?? "");

  const isFlangeClamp = repairType === "flange_clamp";
  const flangeClampDefectData = isFlangeClamp ? (sections.defectDetails.data as FlangeClampDefectDetailsData) : null;
  const flangeClampType = flangeClampDefectData?.flange_clamp_type ?? null;

  const isVoidOnlyClampType = flangeClampType ? VOID_ONLY_CLAMP_TYPES.includes(flangeClampType) : false;
  const isPerimeterAndVoidClampType = flangeClampType
    ? PERIMETER_AND_VOID_CLAMP_TYPES.includes(flangeClampType)
    : false;

  const forcedSealPreference = isVoidOnlyClampType
    ? "Void Only"
    : isPerimeterAndVoidClampType
      ? "Perimeter and Void"
      : null;

  const cuOptionsQuery = useQuery({
    ...createEditJobOptionsQuery,
    select(response) {
      return {
        sealTypeOptions: response.seal_types,
        sealMaterials: response.seal_materials,
        sealPreferences: response.seal_preferences,
        sealSizes: response.seal_sizes,
      };
    },
  });

  const form = useForm<SealingDetailsSchema>({
    resolver: zodResolver(sealingDetailsSchema),
    values: {
      seal_preference: forcedSealPreference ?? savedSealingDetails.data.seal_preference ?? "",
      seal_type: savedSealingDetails.data.seal_type ?? "",
      seal_size: savedSealingDetails.data.seal_size ?? "",
      seal_material: savedSealingDetails.data.seal_material ?? "",
      sealant: savedSealingDetails.data.sealant ?? "",
      sealant_2: savedSealingDetails.data.sealant_2 ?? "",
    },
  });

  const { handleSubmit, watch, setValue, getValues } = form;

  const sealPreference = watch("seal_preference");
  const sealType = watch("seal_type");

  const filteredSealTypes = useMemo(() => {
    const allSealTypes = cuOptionsQuery.data?.sealTypeOptions ?? [];
    if (isPerimeterAndVoidClampType) {
      return allSealTypes.filter((type) => TRIPLE_SEAL_ALLOWED_SEAL_TYPES.includes(type));
    }
    if (sealPreference === "Void Only") {
      const voidOnlyTypes = allSealTypes.filter((type) => VOID_ONLY_ALLOWED_SEAL_TYPES.includes(type));
      return ["None", ...voidOnlyTypes];
    } else if (["Perimeter", "Perimeter and Void"].includes(sealPreference)) {
      return allSealTypes.filter((type) => PERIMETER_ALLOWED_SEAL_TYPES.includes(type));
    }
    return allSealTypes;
  }, [sealPreference, cuOptionsQuery.data?.sealTypeOptions, isPerimeterAndVoidClampType]);

  // For void-only flange clamp types, we don't show the seal type dropdown
  const showSealTypeDropdown = !isVoidOnlyClampType;

  const allowedSealTypesForSize = useMemo(
    () => ["Packing no groove", "Tubing", "Sealant", "Tapered Crunch Grooves w/ sealant", "Crunch Grooves w/ sealant"],
    []
  );

  const showSealSize = useMemo(
    () => !!sealType && allowedSealTypesForSize.includes(sealType),
    [sealType, allowedSealTypesForSize]
  );

  const allowedSealTypesForMaterial = useMemo(() => ["Packing no groove", "Tubing"], []);

  const showSealMaterial = useMemo(
    () => !!sealType && allowedSealTypesForMaterial.includes(sealType),
    [sealType, allowedSealTypesForMaterial]
  );

  const showSealant = sealPreference === "Perimeter" || sealPreference === "Perimeter and Void";
  const showSealant2 = sealPreference === "Void Only" || sealPreference === "Perimeter and Void";

  const filteredSealMaterials = useMemo(() => {
    if (!sealType || !cuOptionsQuery.data?.sealMaterials) return [];
    const effectiveType = ["Packing no groove"].includes(sealType) ? "Packing" : sealType;
    return cuOptionsQuery.data.sealMaterials.filter((material) => material.type === effectiveType);
  }, [sealType, cuOptionsQuery.data?.sealMaterials]);

  const ZERO_SIZE = 0.0;
  const HALF_INCH_SIZE = 0.5;
  const FIVE_SIXTEENTHS_INCH_SIZE = 0.3125;

  const filteredSealSizes = useMemo(() => {
    let sizes = (cuOptionsQuery.data?.sealSizes ?? []).filter(
      (size): size is number => size !== null && size !== ZERO_SIZE
    );
    if (!sealType || !["Packing no groove"].includes(sealType)) {
      sizes = sizes.filter((size) => size !== HALF_INCH_SIZE && size !== FIVE_SIXTEENTHS_INCH_SIZE);
    }
    return sizes;
  }, [cuOptionsQuery.data?.sealSizes, sealType]);

  useEffect(() => {
    if (forcedSealPreference) {
      setValue("seal_preference", forcedSealPreference);
    }
  }, [forcedSealPreference, setValue]);

  useEffect(() => {
    const currentSealType = getValues("seal_type");
    if (currentSealType && !filteredSealTypes.includes(currentSealType)) {
      setValue("seal_type", "");
    }
  }, [filteredSealTypes, getValues, setValue]);

  useEffect(() => {
    if (!showSealSize) {
      setValue("seal_size", "");
    }
  }, [showSealSize, setValue]);

  useEffect(() => {
    if (!showSealMaterial) {
      setValue("seal_material", "");
    }
  }, [showSealMaterial, setValue]);

  useEffect(() => {
    const currentMaterial = getValues("seal_material");
    if (!currentMaterial) return;

    let cleanCurrent = currentMaterial;
    if (currentMaterial.includes(" - ")) {
      cleanCurrent = currentMaterial.split(" - ")[0];
    }

    const isValid = filteredSealMaterials.some((m) => m.material === cleanCurrent);
    if (!isValid) {
      setValue("seal_material", "");
    } else if (cleanCurrent !== currentMaterial) {
      setValue("seal_material", cleanCurrent);
    }
  }, [filteredSealMaterials, getValues, setValue]);

  useEffect(() => {
    const currentSize = getValues("seal_size");
    if (!currentSize) return;
    const isValid = filteredSealSizes.some((size) => String(size) === currentSize);
    if (!isValid) {
      setValue("seal_size", "");
    }
  }, [filteredSealSizes, getValues, setValue]);

  const handleSaveSealingDetails = (data: SealingDetailsSchema) => {
    setSealingDetails({
      data: {
        ...data,
        ...(forcedSealPreference ? { seal_preference: forcedSealPreference } : {}),
      },
      status: "done",
    });
  };

  if (!isApplicableRepairType) {
    return null;
  }

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedSealingDetails.status}
      title="Seal Details"
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(handleSaveSealingDetails)} className="flex flex-col gap-2">
          <div className="flex w-full justify-between gap-11">
            <div className="flex flex-1 flex-col gap-4">
              {!isFlangeClamp && (
                <FormField
                  control={form.control}
                  name="seal_preference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Seal Preference</FormLabel>
                      <FormControl>
                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                          <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                            <SelectValue placeholder="Select seal preference" />
                          </SelectTrigger>
                          <SelectContent>
                            {cuOptionsQuery.data?.sealPreferences?.map((preference) => (
                              <SelectItem key={preference} value={preference}>
                                {preference}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showSealTypeDropdown && (
                <FormField
                  control={form.control}
                  name="seal_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Seal Type</FormLabel>
                      <FormControl>
                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                          <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                            <SelectValue placeholder="Select seal type" />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredSealTypes.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showSealSize && (
                <FormField
                  control={form.control}
                  name="seal_size"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Seal Size</FormLabel>
                      <FormControl>
                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                          <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                            <SelectValue placeholder="Select seal size" />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredSealSizes.map((size) => (
                              <SelectItem key={size} value={String(size)}>
                                {size}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showSealMaterial && (
                <FormField
                  control={form.control}
                  name="seal_material"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Seal Material</FormLabel>
                      <FormControl>
                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                          <SelectTrigger className={cn(!field.value && "[&>span]:text-gray-400")}>
                            <SelectValue placeholder="Select seal material" />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredSealMaterials.map((material) => (
                              <SelectItem key={material.material} value={material.material}>
                                {material.material}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showSealant && (
                <FormField
                  control={form.control}
                  name="sealant"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sealant</FormLabel>
                      <FormControl>
                        <TextInput placeholder="Sealant" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showSealant2 && (
                <FormField
                  control={form.control}
                  name="sealant_2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sealant 2</FormLabel>
                      <FormControl>
                        <TextInput placeholder="Sealant 2" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>
          <Button type="submit" className="self-end">
            Save
          </Button>
        </form>
      </Form>
    </ExpandableSection>
  );
}
